import { EMode, EPages, Footer, initialDataProvider, TUser, Header, TSeoKeywordData } from '@microfrontends/app-shell-v2'
import { Container, ELanguage, Toast } from '@microfrontends/react-components'
import React, { useEffect, useRef, useState } from 'react'
import { decrypt } from '../../helpers/encode'
import { TMetaHead } from '../../helpers/urlHelper'
import userService, { TAuthUser } from '../../services/userService'
import { useStore } from '../../store'
import { ACTION_SET_COMPANY_INDUSTRY_SELECTION, ACTION_SET_IS_MOBILE, ACTION_SET_USER } from '../../store/actions'
import MetaTags from '../MetaTags'
import NotificationComponent from '../Notification'
import { MasterLayoutWrapper, VContainer } from './MasterLayout.css'

type TMasterLayoutProps = {
    children: React.ReactNode
    langCode: ELanguage
    jsToken: string
    wsToken: string
    isMobileScale: boolean
    companyName?: string
    appMode: EMode
    noOffsetContainer?: boolean
    noPaddingResponsiveContainer?: boolean
    metaHead?: TMetaHead
    seoKeywords: TSeoKeywordData[]
}

const firebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY ?? '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ?? '',
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL ?? '',
    refreshJWTUrl: `${process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL}/my-career-center/employer-message/?action=renewFirebaseJWT`
}

const MIN_LOADING_MS = 400
let isPassMinLoadingTime = false
const FIXED_HEADER_HEIGHT = 74
const PADDING_TOP_BOTTOM_NAV = 32

const MasterLayout = (props: TMasterLayoutProps) => {
    const {
        children,
        langCode = ELanguage.VI,
        jsToken,
        wsToken,
        companyName = '',
        appMode,
        noOffsetContainer = false,
        noPaddingResponsiveContainer = false,
        metaHead,
        seoKeywords
    } = props
    const [globalLoading, setGlobalLoading] = useState(false)

    const containerRef = useRef<HTMLDivElement>(null)

    const { state, dispatch } = useStore()
    const { user } = state

    // Authenticate
    const doAuthenticate = async () => {
        if (!jsToken || !wsToken) {
            // setIsReady(true)
            setGlobalLoading(false)
            return
        }
        const user: TAuthUser | null = await userService.authenticate(jsToken, wsToken)

        if (user) {
            const setUserAndUserRelatedData = (user: TAuthUser) => {
                dispatch({
                    type: ACTION_SET_USER,
                    payload: user
                })
            }

            if (isPassMinLoadingTime) {
                setUserAndUserRelatedData(user)
            } else {
                setTimeout(() => {
                    setUserAndUserRelatedData(user)
                }, MIN_LOADING_MS)
            }
        }
        // setIsReady(true)
        setGlobalLoading(false)
    }
    const initSelectData = async () => {
        await initialDataProvider.initSelectData({
            selectDataKey: ['industry', 'jobFunction', 'level'],
            mode: process.env.NEXT_PUBLIC_APP_MODE as any
        })
        const companyIndustries = initialDataProvider.getIndustries()
        if (companyIndustries.length) {
            dispatch({
                type: ACTION_SET_COMPANY_INDUSTRY_SELECTION,
                payload: companyIndustries
            })
        }
    }

    useEffect(() => {
        setGlobalLoading(true)
        // setMounted(true)
        doAuthenticate()
        initSelectData()

        setTimeout(() => {
            isPassMinLoadingTime = true
        }, MIN_LOADING_MS)

        // Scroll to top
        window.scrollTo(0, 0)

        // Set current state to mobile/tablet
        dispatch({
            type: ACTION_SET_IS_MOBILE,
            payload: typeof window !== 'undefined' ? window.innerWidth < 769 : false
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _addHotJarScript()
    }, [])

    useEffect(() => {
        if (globalLoading) {
            document.body.classList.add('noScroll')
            document.documentElement.classList.add('noScroll')
        } else {
            document.body.classList.remove('noScroll')
            document.documentElement.classList.remove('noScroll')
        }
    }, [globalLoading])

    const notificationProps = {
        langCode: langCode,
        firebaseConfig: firebaseConfig,
        jobSeekerUrl: process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL ?? '',
        user: {
            ...user,
            accessToken: decrypt(user?.accessToken ?? '')
        } as TUser,
        baseMsUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH ?? ''
    }

    const _addScript = (content: string, id?: string, toHead?: boolean) => {
        const script = document.createElement('script')
        if (id) script.id = id
        script.innerHTML = content
        const idExists = document.getElementById(id || '')
        if (idExists === null) {
            if (toHead) {
                document.head.append(script)
            } else {
                if (document.body !== undefined) {
                    document.body.append(script)
                }
            }
        }
    }

    const _addHotJarScript = () => {
        const content = `(function(h,o,t,j,a,r){
                        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                        h._hjSettings={hjid:1632895,hjsv:6};
                        a=o.getElementsByTagName('head')[0];
                        r=o.createElement('script');r.defer=1;
                        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                        a.appendChild(r);
                    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`
        return _addScript(content, 'hotJar')
    }

    return (
        <MasterLayoutWrapper>
            <MetaTags pageName={companyName} metaHead={metaHead} language={langCode} />

            <Header
                mode={appMode}
                langCode={langCode}
                user={user as TUser}
                pageName={EPages.COMPANY_LIST}
                responsive={{ type: 'search-page', cornerColor: '#F9F9Fa' }}
            />

            <VContainer
                id='main-vcontainer'
                ref={containerRef}
                noPaddingResponsiveContainer={noPaddingResponsiveContainer}
            >
                <Container noOffset={noOffsetContainer}>{children}</Container>
            </VContainer>

            <Toast />

            <NotificationComponent {...notificationProps} />

            <Footer mode={appMode} langCode={langCode} user={user as any} seoKeywordData={seoKeywords}/>
        </MasterLayoutWrapper>
    )
}

export default MasterLayout
